import type { MacroregionName } from '~/config/cities/constants';
import type { PropertyFilters } from '~/core/entities/property/filters';

import { MACROREGIONS } from '~/config/cities/constants';
import { normalizeStringBase } from '~~/utils';

export interface MacroregionMapViewport {
	macroregion: MacroregionName;
	center: { lat: number; lng: number };
	zoom: number;
}

const VIEWPORTS: Record<
	MacroregionName,
	Omit<MacroregionMapViewport, 'macroregion'>
> = {
	'São Paulo': { center: { lat: -23.585, lng: -46.69 }, zoom: 12 },
	Interior: { center: { lat: -23.23, lng: -47.235 }, zoom: 10 },
	Curitiba: { center: { lat: -25.43, lng: -49.29 }, zoom: 12 },
	'Litoral Norte': { center: { lat: -23.78, lng: -45.755 }, zoom: 10 },
	'Litoral Sul': { center: { lat: -23.96, lng: -46.245 }, zoom: 11 },
	Alphaville: { center: { lat: -23.47, lng: -46.875 }, zoom: 12 }
};

const CITY_SLUGS: Record<string, MacroregionName> = {
	'sao-paulo-sp-brasil': MACROREGIONS.SAO_PAULO,
	'curitiba-pr-brasil': MACROREGIONS.CURITIBA
};

const STATE_SLUGS: Record<string, string> = {
	'sp-brasil': 'sp',
	'pr-brasil': 'pr'
};

function locationValues(values: string[] = []): string[] {
	return [...new Set(values.map(normalizeStringBase).filter(Boolean))];
}

function findMacroregion(
	value: string | undefined
): MacroregionName | undefined {
	if (value === 'capital') return MACROREGIONS.SAO_PAULO;
	return Object.values(MACROREGIONS).find(
		macroregion => normalizeStringBase(macroregion) === value
	);
}

function findCityMacroregion(city: string | undefined) {
	const macroregion = findMacroregion(city);
	return macroregion === MACROREGIONS.SAO_PAULO ||
		macroregion === MACROREGIONS.CURITIBA
		? macroregion
		: undefined;
}

/** Identifies broad searches only; the caller preserves an explicit map viewport. */
export function getMacroregionMapViewport(
	filters: PropertyFilters
): MacroregionMapViewport | undefined {
	if (
		locationValues(filters.regions).length ||
		filters.addressSlug?.trim() ||
		filters.poiLocationLat !== undefined ||
		filters.poiLocationLng !== undefined ||
		filters.h3Ids?.length ||
		filters.pluscodesGroup?.length ||
		filters.commercialIds?.length
	)
		return undefined;

	const cities = locationValues(filters.cities);
	const states = locationValues(filters.states);
	const macroregions = locationValues(filters.macroregions).map(
		findMacroregion
	);
	const distinctMacroregions = [...new Set(macroregions)];
	if (
		cities.length > 1 ||
		states.length > 1 ||
		distinctMacroregions.length > 1 ||
		macroregions.includes(undefined)
	)
		return undefined;

	const selectedMacroregion = distinctMacroregions[0];
	const cityMacroregion = findCityMacroregion(cities[0]);
	if (cities.length && !cityMacroregion) return undefined;
	if (
		selectedMacroregion &&
		cityMacroregion &&
		selectedMacroregion !== cityMacroregion
	)
		return undefined;

	const slug = filters.locationSlug?.trim().toLowerCase();
	const slugMacroregion = slug ? CITY_SLUGS[slug] : undefined;
	const slugState = slug ? STATE_SLUGS[slug] : undefined;
	// Unknown slugs can identify a neighborhood, a city, a street or a condominium.
	if (slug && !slugMacroregion && !slugState) return undefined;

	const macroregion = selectedMacroregion || cityMacroregion || slugMacroregion;
	if (!macroregion) return undefined;

	// The URL parser supplies São Paulo as a default even with another explicit region.
	const hasResidualDefaultSlug =
		slug === 'sao-paulo-sp-brasil' &&
		Boolean(selectedMacroregion || cityMacroregion);
	if (
		slugMacroregion &&
		slugMacroregion !== macroregion &&
		!hasResidualDefaultSlug
	)
		return undefined;

	const state = macroregion === MACROREGIONS.CURITIBA ? 'pr' : 'sp';
	if ((states[0] && states[0] !== state) || (slugState && slugState !== state))
		return undefined;

	const viewport = VIEWPORTS[macroregion];
	return { macroregion, center: { ...viewport.center }, zoom: viewport.zoom };
}
